import configs from "../configs.js";
import {
  clearDashboardBridgeToken,
  getDashboardBridgeTokenEntry,
  getDashboardBridgeToken,
  isDashboardBridgeConfigured,
  renewDashboardBridgeToken,
  renewDashboardBridgeTokenEntry
} from "./auth/dashboardBridgeAuth.js";
import { createDpopProof } from "./auth/dpopProof.js";

export function insertSeparator(children, separatorFn) {
  if (!Array.isArray(children)) {
    return children;
  }

  const length = children.length;

  if (length === 1) {
    return children[0];
  }

  return children.reduce((acc, item, idx) => {
    acc.push(item);

    if (idx !== length - 1) {
      acc.push(separatorFn(idx));
    }

    return acc;
  }, []);
}

export function unique(arr, maybeComp) {
  const set = new Set();
  const newArr = [];

  let comp = maybeComp;

  if (typeof comp === "undefined") {
    comp = item => item;
  } else if (typeof comp === "string") {
    comp = item => item[maybeComp];
  }

  for (const item of arr) {
    const key = comp(item);

    if (set.has(key)) continue;

    newArr.push(item);

    set.add(key);
  }

  return newArr;
}

export const isApple = /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform);

export const cmdOrCtrlString = isApple ? "⌘" : "Ctrl";

export function getStepSize(event, smallStep, mediumStep, largeStep) {
  if (event.altKey) {
    return smallStep;
  } else if (event.shiftKey) {
    return largeStep;
  }
  return mediumStep;
}

export function clamp(value, min, max) {
  if (value < min) {
    return min;
  } else if (value > max) {
    return max;
  }
  return value;
}

export function toPrecision(value, precision) {
  const p = 1 / precision;
  return Math.round(value * p) / p;
}

// https://stackoverflow.com/a/26188910
export function camelPad(str) {
  return (
    str
      // Look for long acronyms and filter out the last letter
      .replace(/([A-Z]+)([A-Z][a-z])/g, " $1 $2")
      // Look for lower-case letters followed by upper-case letters
      .replace(/([a-z\d])([A-Z])/g, "$1 $2")
      // Look for lower-case letters followed by numbers
      .replace(/([a-zA-Z])(\d)/g, "$1 $2")
      .replace(/^./, function (str) {
        return str.toUpperCase();
      })
      // Remove any white space left around the word
      .trim()
  );
}

// https://stackoverflow.com/a/18650828
export function bytesToSize(bytes) {
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  if (bytes == 0) return "0 Byte";
  const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
  return Math.round(bytes / Math.pow(1024, i), 2) + " " + sizes[i];
}

export function getTokenFromCookie() {
  // get the cookie with the name 'access_token'
  const cookies = document.cookie.split("; ");
  const cookie = cookies.find(cookie => cookie.startsWith(configs.JWT_COOKIE));

  if (!cookie) {
    if (configs.IS_DEV) {
      return "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJodHRwczovL3FhLmV2ZW50ZnkuaW8iLCJpYXQiOjE2ODc0NTYxMTMsImV4cCI6MTcyMzc0NDExMywibmJmIjoxNjg3NDU2MTEzLCJqdGkiOiI5QUVvdmVZclFreEVjRVY5Iiwic3ViIjoiNyJ9.0eeJuA4PY2PFE_44t1bBX9G_kVboxCoDpxspu0f4bUk";
    }

    return null;
  }

  return cookie.split("=")[1];
}

let getAccessTokenSilently = null;
let loginWithPopupFn = null;
let loginWithRedirectFn = null;
const DPOP_FETCH_BRIDGE_MARKER = "__onlandDpopFetchBridge";

export const setGetAccessTokenSilently = fn => {
  getAccessTokenSilently = fn;
};

export const setLoginWithPopup = fn => {
  loginWithPopupFn = fn;
};

export const setLoginWithRedirect = fn => {
  loginWithRedirectFn = fn;
};

export const hasLoginWithRedirect = () => !!loginWithRedirectFn;

function isExpectedSilentAuthError(error) {
  const code = String(error?.error || "").toLowerCase();
  const message = String(error?.message || "").toLowerCase();

  return (
    code === "login_required" ||
    code === "consent_required" ||
    code === "interaction_required" ||
    code === "missing_refresh_token" ||
    code === "invalid_grant" ||
    code === "timeout" ||
    message.includes("login_required") ||
    message.includes("consent_required") ||
    message.includes("interaction_required") ||
    message.includes("missing refresh token") ||
    message.includes("invalid_grant") ||
    message.includes("timeout")
  );
}

function normalizeApiRoot(value) {
  const root = String(value || "").trim();
  if (!root) {
    return "";
  }

  try {
    const url = new URL(root);
    url.hash = "";
    url.search = "";
    return url.toString().replace(/\/+$/, "");
  } catch (_error) {
    return "";
  }
}

function isConfiguredProtectedApiUrl(url) {
  const normalizedRoots = [configs.CUSTOM_API_URL, configs.PERSISTENCE_API_URL, configs.OPENAI_API]
    .map(normalizeApiRoot)
    .filter(Boolean);

  if (!normalizedRoots.length) {
    return false;
  }

  let requestUrl = "";
  try {
    requestUrl = new URL(url).toString();
  } catch (_error) {
    return false;
  }

  return normalizedRoots.some(root => requestUrl === root || requestUrl.startsWith(`${root}/`));
}

function getFetchUrl(input) {
  if (typeof input === "string") {
    return input;
  }

  return input?.url || "";
}

function getFetchMethod(input, init = {}) {
  return init.method || input?.method || "GET";
}

async function buildDpopAwareFetchOptions(input, init = {}) {
  const url = getFetchUrl(input);
  const bridgeEntry = getDashboardBridgeTokenEntry();
  if (
    !url ||
    !isConfiguredProtectedApiUrl(url) ||
    !bridgeEntry?.token
  ) {
    return init;
  }

  const headers = new Headers(init.headers || input?.headers || {});
  const authorization = headers.get("Authorization");
  if (authorization !== `Bearer ${bridgeEntry.token}` && authorization !== `DPoP ${bridgeEntry.token}`) {
    return init;
  }

  const method = getFetchMethod(input, init);
  headers.set("Authorization", `DPoP ${bridgeEntry.token}`);
  headers.set("DPoP", await createDpopProof({ url, method, accessToken: bridgeEntry.token }));
  return {
    ...init,
    headers
  };
}

function patchFetchTarget(target) {
  if (!target || typeof target.fetch !== "function" || target.fetch[DPOP_FETCH_BRIDGE_MARKER]) {
    return;
  }

  const originalFetch = target.fetch;
  const dpopFetch = async (input, init = {}) => {
    const options = await buildDpopAwareFetchOptions(input, init);
    return originalFetch(input, options);
  };
  dpopFetch[DPOP_FETCH_BRIDGE_MARKER] = true;
  dpopFetch.__originalFetch = originalFetch;
  target.fetch = dpopFetch;
}

export function installDpopFetchBridge() {
  if (typeof globalThis !== "undefined") {
    patchFetchTarget(globalThis);
  }
  if (typeof window !== "undefined" && window !== globalThis) {
    patchFetchTarget(window);
  }
}

export async function getAuth0Token({ suppressExpectedErrors = true } = {}) {
  const bridgeToken = isDashboardBridgeConfigured()
    ? await renewDashboardBridgeToken().catch(() => null)
    : getDashboardBridgeToken();
  if (bridgeToken) {
    installDpopFetchBridge();
    return bridgeToken;
  }

  if (isDashboardBridgeConfigured()) {
    return null;
  }

  if (!getAccessTokenSilently) {
    return null;
  }

  let token = null;

  try {
    token = await getAccessTokenSilently({
      audience: configs.AUTH0_AUDIENCE,
      scope: "openid profile email offline_access"
    });
  } catch (e) {
    if (!suppressExpectedErrors || !isExpectedSilentAuthError(e)) {
      console.error("Failed to get token silently:", e);
    }
    // TODO: Try to login again with api.reauthenticate();
    return null;
  }

  return token;
}

export async function getAuthHeaders(url, method = "GET", options = {}) {
  const bridgeEntry = isDashboardBridgeConfigured()
    ? await renewDashboardBridgeTokenEntry().catch(() => null)
    : getDashboardBridgeTokenEntry();

  if (bridgeEntry?.token) {
    if (!url) {
      throw new Error("getAuthHeaders requires url when using DPoP.");
    }

    return {
      Authorization: `DPoP ${bridgeEntry.token}`,
      DPoP: await createDpopProof({ url, method, accessToken: bridgeEntry.token })
    };
  }

  const token = await getAuth0Token(options);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function clearAuthToken() {
  clearDashboardBridgeToken();
}

installDpopFetchBridge();

export function loginWithPopup(options) {
  if (!loginWithPopupFn) {
    return null;
  }

  return loginWithPopupFn(options);
}

export function loginWithRedirect(options) {
  if (!loginWithRedirectFn) {
    return null;
  }

  return loginWithRedirectFn(options);
}

export function deleteCookie(cookieName) {
  document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

export function getMetaverseToken() {
  const LOCAL_STORE_KEY = "___hubs_store";
  const value = localStorage.getItem(LOCAL_STORE_KEY);

  if (!value) {
    return null;
  }

  const store = JSON.parse(value);

  if (!store || !store.credentials || !store.credentials.token) {
    return null;
  }

  return store.credentials.token;
}

function isIpadOS() {
  return window.navigator.maxTouchPoints && window.navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform);
}

// lifted from https://github.com/aframevr/aframe/blob/master/src/utils/device.js to ensure consistency
export function isIOS() {
  return (
    /iPad|iPhone|iPod/.test(window.navigator.platform) ||
    (/Safari/.test(window.navigator.userAgent) && !/Chrome/.test(window.navigator.userAgent)) ||
    isIpadOS()
  );
}

export const convertToArrayOfObjects = obj => {
  return Object.keys(obj).map(key => ({
    key: key,
    value: obj[key],
    label: obj[key]
  }));
};

export const getPath = path => {
  return configs.ROUTER_BASE_PATH + path;
};

export const isPublicActivationPath = (pathname, routerBasePath) => {
  const basePath = routerBasePath || "";
  const activationPrefix = `${basePath}/activations/`;
  if (!pathname.startsWith(activationPrefix)) return false;
  const remainder = pathname.slice(activationPrefix.length).replace(/\/+$/, "");
  const segments = remainder.split("/").filter(Boolean);

  // Public paths:
  // - /activations/:slug (1 segment)
  // - /activations/:slug/embed (2 segments, second is "embed")
  // - /activations/:slug/presentations/:presentationSlug (3 segments, second is "presentations")
  return (
    segments.length === 1 ||
    (segments.length === 2 && segments[1] === "embed") ||
    (segments.length === 3 && segments[1] === "presentations")
  );
};

export const isActivationPath = (pathname, routerBasePath) => {
  const basePath = routerBasePath || "";
  const activationPrefix = `${basePath}/activations/`;
  return pathname.startsWith(activationPrefix);
};
